import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  CopyButton,
  Group,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconEye, IconEyeOff, IconKey, IconRefresh } from '@tabler/icons-react';
import { apiService } from '@/api/services/ApiService';
import { getApiMessage, isNetworkError } from '@/api/errors';
import { API_TOKEN_QUERY_KEY, useApiToken } from '@/hooks/useApiToken';
import { MONO_INPUT_STYLES } from '@/styles/theme';

// Вкладка «API-доступ»: токен замаскирован + показать/копировать; регенерация через confirm-модал.
export default function ApiAccessTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: token, isLoading, isError, refetch } = useApiToken();
  const [revealed, setRevealed] = useState(false);

  const regenerate = useMutation({
    mutationFn: () => apiService.regenerateToken(),
    onSuccess: (newToken) => {
      queryClient.setQueryData(API_TOKEN_QUERY_KEY, newToken);
      setRevealed(true); // показать новый ключ, чтобы пользователь успел скопировать
      notifications.show({ color: 'lime', message: t('app.profile.api.regenerated') });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        message: isNetworkError(error)
          ? t('auth.errors.generic')
          : (getApiMessage(error) ?? t('auth.errors.generic')),
      });
    },
  });

  function confirmRegenerate(): void {
    modals.openConfirmModal({
      title: t('app.profile.api.regenerate'),
      children: <Text size="sm">{t('app.profile.api.regenerateWarning')}</Text>,
      labels: { confirm: t('app.profile.api.regenerate'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => regenerate.mutate(),
    });
  }

  const display = token ? (revealed ? token : '•'.repeat(Math.min(token.length, 32))) : '';

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={4}>{t('app.profile.api.title')}</Title>
          <Text c="gray.7" size="sm">
            {t('app.profile.api.desc')}
          </Text>
        </Stack>

        {isLoading ? (
          <Skeleton h={48} />
        ) : isError ? (
          <Alert color="red" variant="light">
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm">{t('auth.errors.generic')}</Text>
              <Button variant="subtle" size="compact-sm" onClick={() => refetch()}>
                {t('common.retry')}
              </Button>
            </Group>
          </Alert>
        ) : token ? (
          <>
            <TextInput
              label={t('app.profile.api.key')}
              readOnly
              value={display}
              styles={MONO_INPUT_STYLES}
              rightSectionWidth={76}
              rightSectionPointerEvents="all"
              rightSection={
                <Group gap={2} wrap="nowrap">
                  <Tooltip label={revealed ? t('auth.password.hide') : t('auth.password.show')}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => setRevealed((value) => !value)}
                      aria-label={revealed ? t('auth.password.hide') : t('auth.password.show')}
                    >
                      {revealed ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                    </ActionIcon>
                  </Tooltip>
                  <CopyButton value={token} timeout={1500}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? t('app.profile.api.copied') : t('app.profile.api.copy')}>
                        <ActionIcon
                          variant="subtle"
                          color={copied ? 'lime' : 'gray'}
                          onClick={copy}
                          aria-label={t('app.profile.api.copy')}
                        >
                          {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
              }
            />
            <Button
              leftSection={<IconRefresh size={18} />}
              w="fit-content"
              loading={regenerate.isPending}
              onClick={confirmRegenerate}
            >
              {t('app.profile.api.regenerate')}
            </Button>
          </>
        ) : (
          <Button
            leftSection={<IconKey size={18} />}
            w="fit-content"
            loading={regenerate.isPending}
            onClick={() => regenerate.mutate()}
          >
            {t('app.profile.api.generate')}
          </Button>
        )}
      </Stack>
    </Card>
  );
}
